import { clearLiteJwtToken } from './liteAuthToken.js';

interface LiteErrorBody {
  code?: string;
  message?: string;
  error?: string;
  details?: {
    validationErrors?: Array<{ field?: string; message?: string }>;
    summary?: string;
  };
}

/**
 * 從後端錯誤 body 取出可顯示的字串（支援多種格式）
 */
function getMessageFromErrorBody(body: LiteErrorBody): string | undefined {
  if (typeof body.message === 'string' && body.message.trim())
    return body.message.trim();
  if (typeof body.error === 'string' && body.error.trim())
    return body.error.trim();
  const first = body.details?.validationErrors?.[0]?.message;
  if (typeof first === 'string' && first.trim()) return first.trim();
  if (typeof body.details?.summary === 'string' && body.details.summary.trim())
    return body.details.summary.trim();
  return undefined;
}

/**
 * 處理 Lite API 回傳的錯誤：
 * - 若為 401 且錯誤碼為 LITE_USER_NOT_FOUND / LITE_USER_MISMATCH：清除 JWT 並導向登入入口。
 * - 其他錯誤：丟出帶有適當訊息的 Error。
 */
export async function handleLiteErrorResponse(
  response: Response,
  options?: { defaultMessage?: string }
): Promise<never> {
  let body: LiteErrorBody = {};

  try {
    const text = await response.text();
    if (text && text.trim()) {
      body = JSON.parse(text) as LiteErrorBody;
    }
  } catch {
    // 若非 JSON，保持 body 為空物件
  }

  const code = body.code;
  const messageFromBody = getMessageFromErrorBody(body);
  const baseMessage =
    messageFromBody ||
    (response.status === 400 ? `請求錯誤（400）` : undefined) ||
    options?.defaultMessage ||
    '請求失敗，請稍後再試';

  if (response.status === 401) {
    const isTokenExpired = code === 'TOKEN_EXPIRED';
    const isUserMismatch =
      code === 'LITE_USER_NOT_FOUND' || code === 'LITE_USER_MISMATCH';

    if (isTokenExpired || isUserMismatch) {
      clearLiteJwtToken();

      if (typeof window !== 'undefined') {
        const path = window.location.pathname || '';
        const isSquareContext =
          path.startsWith('/lite/shop') ||
          path.startsWith('/lite/square') ||
          path.startsWith('/lite/new-square') ||
          path.startsWith('/lite/square-choice') ||
          path.startsWith('/lite/square-enter');

        if (isSquareContext) {
          window.location.href = '/lite/new-square';
        } else {
          // TOKEN_EXPIRED 或 user mismatch：重新載入，讓 LIFF 重新執行 register 取得新 token
          window.location.reload();
        }
      }

      const authError = new Error(
        isTokenExpired
          ? '登入已過期，正在重新載入…'
          : baseMessage || '登入狀態已失效，請重新登入後再試'
      ) as Error & { code?: string; status?: number };
      authError.code = code;
      authError.status = response.status;
      throw authError;
    }
  }

  const error = new Error(
    baseMessage || `請求失敗（${response.status}）`
  ) as Error & { code?: string; status?: number };
  if (code) {
    error.code = code;
  }
  error.status = response.status;
  throw error;
}
